import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import { toast } from "sonner";

const WA_NUMBER = "573043381885";

const initial = {
  name: "",
  email: "",
  phone: "",
  company: "",
  interest: "",
  message: "",
};

export default function LeadModal({ open, onOpenChange }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleClose = (v) => {
    if (!loading) {
      onOpenChange(v);
      if (!v) setTimeout(() => { setSuccess(false); setForm(initial); }, 300);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(lang === "es" ? "Completa nombre y correo" : "Please fill name and email");
      return;
    }
    setLoading(true);

    // Build WhatsApp message with form data
    const lines = [
      `Hola Reinvente 👋 Me llegaron a través de la landing page.`,
      ``,
      `*Nombre:* ${form.name}`,
      `*Email:* ${form.email}`,
      form.phone  ? `*Teléfono:* ${form.phone}` : null,
      form.company ? `*Empresa:* ${form.company}` : null,
      form.interest ? `*Interés:* ${form.interest}` : null,
      form.message ? `*Mensaje:* ${form.message}` : null,
    ].filter(Boolean).join("\n");

    const waUrl = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(lines)}`;

    setTimeout(() => {
      window.open(waUrl, "_blank", "noreferrer");
      setLoading(false);
      setSuccess(true);
      toast.success(t.form.success_title);
    }, 600);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="lead-modal"
        className="!max-w-xl !bg-[#0b1220] !border-white/10 text-white rounded-2xl glass-strong p-0 overflow-hidden"
      >
        <div className="relative">
          <div className="absolute -top-24 -right-20 h-60 w-60 rounded-full bg-brand-blue/25 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-20 h-60 w-60 rounded-full bg-brand-orange/20 blur-3xl pointer-events-none" />

          {!success ? (
            <form onSubmit={submit} className="relative p-6 sm:p-8">
              <DialogHeader>
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-blue font-mono-tight mb-2">
                  Reinvente
                </div>
                <DialogTitle className="font-display text-2xl sm:text-[28px] font-black tracking-tight text-white leading-tight">
                  {t.form.title}
                </DialogTitle>
                <DialogDescription className="text-white/55 text-sm leading-relaxed">
                  {t.form.sub}
                </DialogDescription>
              </DialogHeader>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={t.form.name} required>
                  <input
                    data-testid="lead-form-name"
                    value={form.name}
                    onChange={update("name")}
                    type="text"
                    required
                    className="input"
                  />
                </Field>
                <Field label={t.form.email} required>
                  <input
                    data-testid="lead-form-email"
                    value={form.email}
                    onChange={update("email")}
                    type="email"
                    required
                    className="input"
                  />
                </Field>
                <Field label={t.form.phone}>
                  <input
                    data-testid="lead-form-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    type="tel"
                    className="input"
                  />
                </Field>
                <Field label={t.form.company}>
                  <input
                    data-testid="lead-form-company"
                    value={form.company}
                    onChange={update("company")}
                    type="text"
                    className="input"
                  />
                </Field>
                <Field label={t.form.interest} className="sm:col-span-2">
                  <select
                    data-testid="lead-form-interest"
                    value={form.interest}
                    onChange={update("interest")}
                    className="input appearance-none cursor-pointer"
                  >
                    <option value="" className="bg-[#0b1220]">—</option>
                    {t.form.interests.map((i) => (
                      <option key={i} value={i} className="bg-[#0b1220]">{i}</option>
                    ))}
                  </select>
                </Field>
                <Field label={t.form.message} className="sm:col-span-2">
                  <textarea
                    data-testid="lead-form-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={3}
                    className="input resize-none"
                  />
                </Field>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="lead-form-submit"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full text-sm font-semibold text-white btn-primary-glow disabled:opacity-70"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> {t.form.submitting}
                  </>
                ) : (
                  <>
                    {t.form.submit} <Send className="h-3.5 w-3.5" />
                  </>
                )}
              </button>

              <style>{`
                .input {
                  width: 100%;
                  background: rgba(255,255,255,0.03);
                  border: 1px solid rgba(255,255,255,0.1);
                  border-radius: 10px;
                  padding: 11px 13px;
                  font-size: 14px;
                  color: #fff;
                  outline: none;
                  transition: all .2s ease;
                }
                .input:focus {
                  border-color: rgba(23,105,255,0.7);
                  box-shadow: 0 0 0 3px rgba(23,105,255,0.18);
                  background: rgba(255,255,255,0.05);
                }
                .input::placeholder { color: rgba(255,255,255,0.35); }
                select.input option { color: #fff; }
              `}</style>
            </form>
          ) : (
            <div className="relative p-10 text-center" data-testid="lead-form-success">
              <div className="mx-auto h-14 w-14 grid place-items-center rounded-full bg-brand-blue/15 ring-1 ring-brand-blue/40 mb-5">
                <CheckCircle2 className="h-7 w-7 text-brand-blue" strokeWidth={1.8} />
              </div>
              <h3 className="font-display text-2xl font-black tracking-tight text-white">
                {t.form.success_title}
              </h3>
              <p className="mt-3 text-white/60 leading-relaxed max-w-sm mx-auto text-sm">
                {t.form.success_desc}
              </p>
              <button
                onClick={() => handleClose(false)}
                className="mt-7 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white btn-ghost-glass"
              >
                {lang === "es" ? "Cerrar" : "Close"}
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] uppercase tracking-[0.18em] text-white/55 font-mono-tight mb-1.5">
        {label} {required && <span className="text-brand-orange">*</span>}
      </span>
      {children}
    </label>
  );
}
